// src/components/invoiceFormats/DotMatrixFormat.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { httpService } from "../../services/httpService";
import { useProfile } from "../../context/ProfileContext";

/* ================================================================
   API — snapshot endpoint
   ================================================================ */
const fetchInvoiceSnapshot = async (orderId) => {
  const res = await httpService.get(
    `/invoice/getInvoiceSnapshotByOrderId/${orderId}`,
  );
  return res?.data ?? res;
};

const PRINTERS = {
  dotmatrix: { width: 680, showTractor: true, label: "Dot Matrix" },
  laser: { width: 760, showTractor: false, label: "Laser" },
  thermal: { width: 280, showTractor: false, label: "Thermal" },
};

/* ================================================================
   HELPERS
   ================================================================ */
const formatAddress = (addr) => {
  if (!addr) return "";
  if (typeof addr === "string") return addr.trim();
  if (Array.isArray(addr))
    return addr.map(formatAddress).filter(Boolean).join(", ");

  if (typeof addr === "object") {
    if (
      typeof addr.full_address === "string" &&
      addr.full_address.trim() !== ""
    )
      return addr.full_address;
    if (typeof addr.address === "string" && addr.address.trim() !== "")
      return addr.address;
    if (addr.officeAddress || addr.homeAddress) {
      const off = formatAddress(addr.officeAddress);
      const home = formatAddress(addr.homeAddress);
      return [off, home].filter(Boolean).join(", ");
    }
    if (
      addr.buildingName ||
      addr.locality ||
      addr.city ||
      addr.state ||
      addr.pincode
    ) {
      const parts = [
        addr.buildingName,
        addr.floor,
        addr.locality,
        addr.city,
        addr.state,
        addr.pincode,
      ]
        .filter(Boolean)
        .map((s) => (typeof s === "string" ? s.trim() : s));
      return parts.join(", ");
    }
    if (typeof addr.address === "object") return formatAddress(addr.address);
    return Object.values(addr)
      .filter((v) => typeof v === "string" && v.trim() !== "")
      .join(", ");
  }
  return String(addr);
};

const cleanText = (t) => formatAddress(t);

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (v) => `₹${num(v).toFixed(2)}`;

const fmtDate = (d) => {
  try {
    return d ? format(new Date(d), "dd/MM/yyyy") : "—";
  } catch {
    return "—";
  }
};

const fmtTime = (d) => {
  try {
    return d ? format(new Date(d), "hh:mm a") : "—";
  } catch {
    return "—";
  }
};

const numberToWords = (numVal) => {
  const value = num(numVal);
  if (!value) return "Zero Rupees Only";
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const w = (n, s) => {
    let str = n > 19 ? b[Math.floor(n / 10)] + " " + a[n % 10] : a[n];
    if (n) str += " " + s;
    return str;
  };
  const intPart = Math.floor(value);
  const decPart = Math.round((value - intPart) * 100);
  let out = "";
  out += w(Math.floor(intPart / 10000000), "Crore ");
  out += w(Math.floor((intPart / 100000) % 100), "Lakh ");
  out += w(Math.floor((intPart / 1000) % 100), "Thousand ");
  out += w(Math.floor((intPart / 100) % 10), "Hundred ");
  if (intPart > 100 && intPart % 100) out += "and ";
  out += w(Math.floor(intPart % 100), "");
  let final = "Rupees " + out.trim();
  if (decPart > 0) final += ` and ${w(decPart, "")} Paise`;
  return final + " Only";
};

/* ================================================================
   NORMALIZE SNAPSHOT
   ================================================================ */
const classifyChild = (it) => {
  const src = String(it.source || "").toLowerCase();
  const st = String(it.service_type || "").toLowerCase();
  if (src === "nested_product" || st === "product" || it.is_product)
    return "product";
  if (src === "nested_addon" || st === "addon" || it.is_addon) return "addon";
  if (src === "other_charge" || st === "other_charge" || it.is_other_charge)
    return "other";
  return "product";
};

const normalizeSnapshot = (snapRaw) => {
  const snap = snapRaw?.data ?? snapRaw ?? {};
  const vendor = snap.vendor ?? {};
  const billTo = snap.bill_to ?? {};
  const summary = snap.summary ?? {};
  const invoiceAmount = snap.invoice_amount ?? {};

  const vendorAddress = formatAddress(vendor.address);
  const rawItems = Array.isArray(snap.items) ? snap.items : [];

  const parentByRef = new Map();
  const parents = [];

  for (const it of rawItems) {
    if (it.is_child_row) continue;
    const key = String(it.ref_id ?? it._id ?? parents.length);
    const parent = {
      _id: it._id ?? key,
      _key: key,
      service_name: it.description ?? it.service_name ?? "Item",
      item_type: it.service_type ?? "",
      hsn_code: it.hsn_code ?? "",
      expiry: it.expiry ?? "",
      batch_no: it.batch_number ?? it.batch_no ?? "",
      quantity: num(it.quantity),
      unit_price: num(it.mrp),
      price_ex_tax: num(it.unit_price_excl_tax ?? it.mrp),
      gst: num(it.gst_amount),
      gst_percent: num(it.tax_percentage),
      discount: num(it.discount_amount),
      total_price: num(it.total_amount ?? it.amount),
      products: [],
      addons: [],
      otherCharges: [],
    };
    parents.push(parent);
    parentByRef.set(key, parent);
  }

  const isProductInvoice =
    parents.length > 0 &&
    parents.every((p) => String(p.item_type || "").toLowerCase() === "product");

  for (const it of rawItems) {
    if (!it.is_child_row) continue;
    const parentKey = String(it.parent_ref_id ?? "");
    const parent = parentByRef.get(parentKey) ?? parents[parents.length - 1];
    if (!parent) continue;
    const kind = classifyChild(it);
    const child = {
      _id:
        it._id ??
        `${parent._key}-${kind}-${parent[kind === "product" ? "products" : kind === "addon" ? "addons" : "otherCharges"].length}`,
      quantity: num(it.quantity ?? 1),
      unit_price: num(it.mrp),
      price_ex_tax: num(it.unit_price_excl_tax ?? it.mrp),
      gst: num(it.gst_amount),
      gst_percent: num(it.tax_percentage),
      discount: num(it.discount_amount),
      total_price: num(it.total_amount ?? it.amount),
      hsn_code: it.hsn_code ?? "",
      expiry: it.expiry ?? "",
      batch_no: it.batch_number ?? it.batch_no ?? "",
    };
    if (kind === "product")
      parent.products.push({
        ...child,
        product_name: it.description ?? it.service_name ?? "Product",
      });
    else if (kind === "addon")
      parent.addons.push({
        ...child,
        name: it.description ?? it.service_name ?? "Addon",
      });
    else
      parent.otherCharges.push({
        ...child,
        description: it.description ?? it.service_name ?? "Other Charge",
        amount: num(it.mrp ?? it.total_amount),
      });
  }

  for (const it of rawItems) {
    if (it.is_child_row) continue;
    const key = String(it.ref_id ?? it._id ?? "");
    const parent = parentByRef.get(key);
    if (!parent) continue;
    for (const c of it.children ?? []) {
      const kind = classifyChild(c);
      const child = {
        _id:
          c._id ??
          `${parent._key}-child-${Math.random().toString(36).slice(2, 7)}`,
        quantity: num(c.quantity ?? 1),
        unit_price: num(c.mrp),
        price_ex_tax: num(c.unit_price_excl_tax ?? c.mrp),
        gst: num(c.gst_amount),
        gst_percent: num(c.tax_percentage),
        discount: num(c.discount_amount),
        total_price: num(c.total_amount ?? c.amount),
        hsn_code: c.hsn_code ?? "",
        expiry: c.expiry ?? "",
        batch_no: c.batch_number ?? c.batch_no ?? "",
      };
      if (kind === "product")
        parent.products.push({
          ...child,
          product_name: c.description ?? c.name ?? "Product",
        });
      else if (kind === "addon")
        parent.addons.push({
          ...child,
          name: c.description ?? c.name ?? "Addon",
        });
      else
        parent.otherCharges.push({
          ...child,
          description: c.description ?? c.name ?? "Other Charge",
          amount: num(c.mrp ?? c.total_amount),
        });
    }
  }

  const totalQty = parents.reduce((s, it) => {
    let q = it.quantity;
    it.products.forEach((p) => (q += p.quantity));
    it.addons.forEach((a) => (q += a.quantity));
    return s + q;
  }, 0);

  const grandTotal = num(
    num(snap.amount_after_adjustment) > 0
      ? snap.amount_after_adjustment
      : snap.grand_total || snap.amount_after_adjustment || 0,
  );

  const netAmount = num(snap.net_amount);
  const totalDiscount = num(
    summary.item_discount_amount ?? snap.discount_amount ?? 0,
  );
  const calculatedSubTotal = netAmount + totalDiscount;

  const payments = (snap.payments ?? []).map((p) => {
    const rawPaid = num(p.paid_amount);
    const isPaidStatus =
      String(snap.payment_status || "").toLowerCase() === "paid";
    const resolvedAmount =
      rawPaid > 0
        ? rawPaid
        : isPaidStatus
          ? num(snap.paid_amount) > 0
            ? num(snap.paid_amount)
            : grandTotal
          : 0;
    return {
      mode: p.payment_mode ?? "—",
      amount: resolvedAmount,
      waived: num(p.waived_amount),
      date: p.payment_date ?? null,
      ref: p.reference_number ?? null,
    };
  });

  let paymentMode = "—";
  if (payments.length > 0) {
    const modes = [...new Set(payments.map((p) => p.mode).filter(Boolean))];
    if (modes.length) paymentMode = modes.join(", ");
  }

  return {
    invoice_no: snap.invoice_no ?? "—",
    order_number: snap.invoice_no ?? snap.order_no ?? "—",
    order_no: snap.order_no ?? "—",
    createdAt:
      snap.invoice_date ?? snap.audit?.created_at ?? new Date().toISOString(),
    is_closed: !!snap.is_closed,
    header_date_label: snap.is_closed ? "Closed On" : "Date & Time",
    invoice_type: snap.invoice_type ?? "PROFORMA",
    is_product_invoice: isProductInvoice,
    order_type: "",
    payment_status: String(snap.payment_status ?? "unpaid").toLowerCase(),
    status: String(snap.status ?? "UNPAID").toUpperCase(),
    vendor: {
      name: vendor.name ?? vendor.brand_name ?? "—",
      ownerName: "",
      phone: vendor.phone ?? "",
      email: vendor.email ?? "",
      gstNumber: vendor.gstin ?? "",
      address: null,
      addressLine: vendorAddress,
      logo: vendor.logo_url ?? vendor.logoDataUrl ?? vendor.logo ?? null,
    },
    vendor_client: {
      first_name: "",
      last_name: "",
      phone: billTo.phone ?? "",
      email: billTo.email ?? "",
      name: billTo.name ?? "",
    },
    items: parents,
    total_qty: totalQty,
    sub_total: num(snap.sub_total ?? calculatedSubTotal),
    net_amount: netAmount,
    total_discount: totalDiscount,
    total_gst: num(snap.tax_amount),
    item_discount: num(summary.item_discount_amount ?? 0),
    order_discount: num(summary.order_discount_amount ?? 0),
    round_off: num(snap.round_off),
    adjustment_amount: num(snap.adjustment_amount),
    grand_total: grandTotal,
    total_paid_amount: num(invoiceAmount.paid_amount ?? snap.paid_amount),
    balance_amount: num(invoiceAmount.balance_amount ?? snap.balance_due),
    waived_amount: num(snap.waived_amount),
    order_payments: payments,
    payments,
    payment_mode: paymentMode,
    amount_in_words:
      snap.amount_in_words ||
      invoiceAmount.amount_in_words ||
      numberToWords(grandTotal),
  };
};

/* ================================================================
   PROFILE EXTRACTOR
   ================================================================ */
const extractProfile = (
  profileData,
  fallbackVendor = {},
  localOverrides = {},
) => {
  const d = profileData || {};
  const data = d.data || d;
  const v = d.vendor || data.vendor || {};
  const profile = data.profile || d.profile || {};
  const dataAddr = data.address || d.address || {};
  const photo = data.photo || d.photo || {};

  const backendOverrides =
    data.invoiceHeaderSettings || d.invoiceHeaderSettings || {};
  const overrides = { ...backendOverrides, ...localOverrides };

  const companyName =
    overrides.name ||
    v.businessName ||
    v.brand_name ||
    v.brand ||
    v.companyName ||
    v.name ||
    profile.businessName ||
    profile.brand ||
    profile.companyName ||
    profile.name ||
    data.businessName ||
    data.brand_name ||
    data.companyName ||
    data.name ||
    fallbackVendor.businessName ||
    fallbackVendor.brand_name ||
    fallbackVendor.companyName ||
    fallbackVendor.name ||
    "";

const safeLogo = (() => {
  // respect an explicit override (including "") before anything else
  if (overrides.logo_url !== undefined && overrides.logo_url !== null)
    return overrides.logo_url;
  if (overrides.logo !== undefined && overrides.logo !== null)
    return overrides.logo;

  return (
    v.logo_url ||
    v.logoDataUrl ||
    v.logo ||
    data.logo_url ||
    data.logoDataUrl ||
    data.logo ||
    profile.logo_url ||
    profile.logoDataUrl ||
    profile.logo ||
    profile.companyLogo ||
    profile.companyLogoUrl ||
    photo.profilePhoto ||
    photo.logo ||
    photo.companyLogo ||
    (Array.isArray(photo.shop) && photo.shop.length > 0 ? photo.shop[0] : "") ||
    fallbackVendor.logo_url ||
    fallbackVendor.logoDataUrl ||
    fallbackVendor.logo ||
    ""
  );
})();

  return {
    name: companyName,
    showSignature:
      overrides.showSignature !== undefined ? overrides.showSignature : true,
    phone:
      overrides.phone ||
      v.phone ||
      profile.phone ||
      data.phone ||
      fallbackVendor.phone ||
      "",
    email:
      overrides.email ||
      v.email ||
      profile.email ||
      data.email ||
      fallbackVendor.email ||
      "",
    gstin:
      overrides.gstin ||
      v.gstin ||
      profile.gstin ||
      data.gstin ||
      d.gstin ||
      fallbackVendor.gstin ||
      "",
    address:
      overrides.address ||
      formatAddress(v.address) ||
      formatAddress(dataAddr) ||
      formatAddress(fallbackVendor.address) ||
      "",
    logo: safeLogo,
    terms:
      overrides.terms ||
      v.terms ||
      profile.terms ||
      "Goods once sold will not be taken back or exchanged. Subject to local jurisdiction.",
  };
};

/* ================================================================
   EDITABLE FIELD + LOGO
   ================================================================ */
const PencilIcon = () => (
  <svg
    className="w-3 h-3"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
);

const EditableField = ({
  value,
  onCommit,
  placeholder = "",
  displayClassName = "",
  inputClassName = "",
  multiline = false,
  rows = 2,
  align = "left",
  isEditable = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const commit = () => {
    onCommit(draft);
    setEditing(false);
  };
  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
  };

  const justify =
    align === "right"
      ? "justify-end"
      : align === "center"
        ? "justify-center"
        : "justify-start";
  const textAlign =
    align === "right"
      ? "text-right"
      : align === "center"
        ? "text-center"
        : "text-left";

  const hasValue =
    value &&
    String(value).trim() !== "" &&
    value !== "—" &&
    value !== "null" &&
    value !== "[object Object]";

  if (!isEditable) {
    if (!hasValue) return null;
    return (
      <div className={`flex items-center gap-1 w-full ${justify}`}>
        <div className={`flex-1 ${textAlign} ${displayClassName}`}>
          <span className={multiline ? "whitespace-pre-wrap" : ""}>
            {value}
          </span>
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div
        className={`no-print flex items-center gap-1 w-full ${justify} my-0.5`}
      >
        <button
          onClick={cancel}
          className="text-[10px] bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded hover:bg-gray-300 font-bold shrink-0"
          title="Cancel"
        >
          X
        </button>
        {multiline ? (
          <textarea
            autoFocus
            rows={rows}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") cancel();
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) commit();
            }}
            className={`flex-1 bg-transparent border-b border-blue-500 focus:outline-none resize-none ${textAlign} ${inputClassName}`}
            placeholder={placeholder}
          />
        ) : (
          <input
            autoFocus
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") cancel();
            }}
            className={`flex-1 bg-transparent border-b border-blue-500 focus:outline-none ${textAlign} ${inputClassName}`}
            placeholder={placeholder}
          />
        )}
        <button
          onClick={commit}
          className="text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-700 font-medium shrink-0"
          title="Save"
        >
          Save
        </button>
      </div>
    );
  }

  if (!hasValue) {
    return (
      <div className={`group flex items-center gap-1 w-full ${justify} h-4`}>
        <button
          onClick={() => setEditing(true)}
          className="no-print opacity-0 group-hover:opacity-100 transition-opacity text-[10px] text-blue-500 hover:underline ml-auto"
        >
          + Add {placeholder}
        </button>
      </div>
    );
  }

  return (
    <div className={`group flex items-center gap-1 w-full ${justify}`}>
      <div className={`flex-1 ${textAlign} ${displayClassName}`}>
        <span className={multiline ? "whitespace-pre-wrap" : ""}>{value}</span>
      </div>
      <button
        onClick={() => setEditing(true)}
        className="no-print opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-blue-600 shrink-0 p-0.5 rounded"
        title={`Edit`}
      >
        <PencilIcon />
      </button>
    </div>
  );
};

const EditableLogo = ({
  value,
  onCommit,
  className = "",
  isEditable = false,
}) => {
  const fileInputRef = useRef(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onCommit(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onCommit("");
  };
  const hasValue =
    value &&
    String(value).trim() !== "" &&
    value !== "null" &&
    value !== "[object Object]";

  if (!isEditable) {
    if (!hasValue) return null;
    return (
      <div className={`${className}`}>
        <div className="w-full h-full flex items-center justify-center">
          <img
            src={value}
            alt="Logo"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      </div>
    );
  }

  if (!hasValue) {
    return (
      <div
        className={`relative group ${className} flex items-center justify-center`}
      >
        <button
          onClick={() => fileInputRef.current?.click()}
          className="no-print opacity-0 group-hover:opacity-100 transition-opacity text-[9px] text-blue-500"
        >
          + Add Logo
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/*"
          className="hidden"
        />
      </div>
    );
  }

  return (
    <div className={`relative group ${className}`}>
      <div className="w-full h-full flex items-center justify-center">
        <img
          src={value}
          alt="Logo"
          className="max-w-full max-h-full object-contain"
        />
      </div>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept="image/*"
        className="hidden"
      />
      <button
        onClick={handleClear}
        className="no-print absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] shadow-sm hover:bg-red-600 z-10"
        title="Remove Logo"
      >
        ✕
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          fileInputRef.current?.click();
        }}
        className="no-print absolute -bottom-1 -right-1 bg-white border border-gray-300 p-0.5 rounded-full shadow-sm text-gray-500 hover:text-blue-600 transition"
        title="Change Logo"
      >
        <PencilIcon />
      </button>
    </div>
  );
};

/* ================================================================
   PRESENTATION
   ================================================================ */
const Tractor = () => (
  <div className="w-[30px] bg-[#f2f2ea] border-x border-dashed border-gray-400 flex flex-col items-center py-2 gap-[10px] print:hidden">
    {Array.from({ length: 60 }).map((_, i) => (
      <span
        key={i}
        className="block w-2.5 h-2.5 rounded-full bg-white border border-gray-400"
      />
    ))}
  </div>
);

const PrintStyles = ({ printerType }) => {
  const isThermal = printerType === "thermal";
  return (
    <style>{`
      @media print {
        @page {
          size: ${isThermal ? "80mm auto" : "auto"};
          margin: ${isThermal ? "0" : "6mm"};
        }
        html, body {
          background: #fff !important;
          margin: 0 !important;
          padding: 0 !important;
          ${isThermal ? "width: 80mm !important; overflow: visible !important;" : ""}
        }
        body * { visibility: hidden !important; }
        #invoice-print-area, #invoice-print-area * { visibility: visible !important; }
        #invoice-print-area {
          position: absolute !important;
          left: 0; top: 0;
          width: ${isThermal ? "80mm" : "100%"} !important;
          border: none !important;
          box-shadow: none !important;
        }
        .no-print { display: none !important; }
        .print-wrapper { ${isThermal ? "display: none !important;" : ""} }
        ${
          isThermal
            ? `#invoice-print-area > div {
                width: 80mm !important;
                padding: 0 2mm !important;
                margin: 0 !important;
                box-shadow: none !important;
                background: #fff !important;
              }`
            : ""
        }
      }
    `}</style>
  );
};

/* ================================================================
   MAIN COMPONENT
   ================================================================ */
export default function DotMatrixFormat({
  demoData = null,
  printerType: printerTypeProp = null,
  hideToolbar = false,
  isEditable = false, // 🟢 Strictly controls editing
}) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { profileData, fetchProfile, vendorId } = useProfile();

  const printerType = (
    printerTypeProp ||
    params.printerType ||
    params.type ||
    window.location.pathname.split("/").filter(Boolean).pop() ||
    "dotmatrix"
  ).toLowerCase();

  const orderId =
    params.orderId ||
    searchParams.get("orderId") ||
    searchParams.get("id") ||
    "";
  const printer = PRINTERS[printerType] ?? PRINTERS.dotmatrix;

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Header fields
  const [profile, setProfile] = useState({
    name: "",
    phone: "",
    email: "",
    gstin: "",
    address: "",
    logo: "",
    signatureName: "",
    showSignature: true,
  });
  const [localOverrides, setLocalOverrides] = useState({});

  // Save UI
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const load = useCallback(async () => {
    if (demoData) {
      setInvoice(normalizeSnapshot(demoData));
      setLoading(false);
      return;
    }
    if (!orderId) {
      setError("No order id provided.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetchInvoiceSnapshot(orderId);
      setInvoice(normalizeSnapshot(res));
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "Failed to load invoice snapshot.",
      );
    } finally {
      setLoading(false);
    }
  }, [orderId, demoData]);

  useEffect(() => {
    load();
  }, [load]);

  // Hydrate Profile
  useEffect(() => {
    const fallbackVendor = demoData?.vendor || {};
    const extracted = extractProfile(
      profileData,
      fallbackVendor,
      localOverrides,
    );
    setProfile(extracted);
  }, [profileData, demoData, localOverrides]);

  // 🟢 INLINE SAVE FUNCTION
  const handleInlineSave = async (field, value) => {
    const updatedProfile = { ...profile, [field]: value };
    setProfile(updatedProfile);

    const payload = {
      name: updatedProfile.name,
      phone: updatedProfile.phone,
      email: updatedProfile.email,
      gstin: updatedProfile.gstin,
      address: updatedProfile.address,
      logo_url: updatedProfile.logo,
      terms: updatedProfile.terms,
      showSignature: updatedProfile.showSignature,
    };

    setSaving(true);
    setSaveError("");
    setSaveSuccess(false);

    try {
      const endpoint = vendorId
        ? `vendorregistration/updateInvoiceHeaderSettings/${vendorId}`
        : "vendorregistration/updateInvoiceHeaderSettings";

      await httpService.put(endpoint, payload);

      setLocalOverrides((prev) => ({ ...prev, ...payload }));

      if (typeof fetchProfile === "function") await fetchProfile();

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (e) {
      setSaveError(
        e?.response?.data?.message || e?.message || "Failed to save.",
      );
      setProfile(profile); // Revert if error
    } finally {
      setSaving(false);
    }
  };

  const orderNo = useMemo(
    () =>
      invoice?.order_no || (invoice?.order_number || "").replace("INV", "ORD"),
    [invoice],
  );

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 text-gray-600 font-mono">
        Loading invoice…
      </div>
    );
  if (error)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 gap-3">
        <p className="text-red-600 font-mono">{error}</p>
        <button
          onClick={load}
          className="px-4 py-2 bg-black text-white rounded text-sm"
        >
          Retry
        </button>
      </div>
    );
  if (!invoice) return null;

  const customerName = cleanText(invoice.vendor_client.name);
  const showExpiry = invoice.is_product_invoice;
  const logoSize = printerType === "thermal" ? "w-10 h-10" : "w-14 h-14";

  const colItem = showExpiry ? "w-[23%]" : "w-[35%]";
  const colExpiry = "w-[12%]";
  const colQty = "w-[6%]";
  const colMrp = "w-[12%]";
  const colDisc = "w-[13%]";
  const colTax = "w-[12%]";
  const colTotal = "w-[22%]";

  const width = printer.width;
  const line = (char = "-") => char.repeat(200);

  return (
    <div className="min-h-screen bg-gray-200 print-wrapper font-mono">
      <PrintStyles printerType={printerType} />

      {/* TOOLBAR */}
      {!hideToolbar && (
        <div className="no-print print:hidden sticky top-0 z-20 bg-white border-b border-gray-300 font-sans">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-sm font-semibold text-gray-800">
                  Invoice Preview
                </h1>
                <p className="text-xs text-gray-500 font-mono">
                  {printer.label} · {invoice.invoice_no || invoice.order_number}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden sm:inline-block text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 font-mono uppercase">
                {printerType}
              </span>
              <button
                onClick={() => window.print()}
                className="px-4 py-1.5 text-sm rounded bg-black text-white hover:bg-gray-800"
              >
                🖨 Print
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CENTERED INVOICE */}
      <div className="flex justify-center p-6">
        <div id="invoice-print-area">
          <div
            className="flex bg-[#fdfdf7] border border-gray-400 shadow-md"
            style={{ width: width + (printer.showTractor ? 60 : 0) }}
          >
            {printer.showTractor && <Tractor />}

            <div
              className="flex-1 py-4 px-2 font-mono text-[12px] leading-[18px] text-black overflow-hidden"
              style={{
                width,
                backgroundImage:
                  "repeating-linear-gradient(0deg,#fdfdf7,#fdfdf7 17px,#e9e9e0 18px)",
              }}
            >
              {/* ─── HEADER (Editable) ─── */}
              <div className="flex items-start gap-2 mb-1">
                <EditableLogo
                  value={profile.logo}
                  onCommit={(val) => handleInlineSave("logo", val)}
                  className={`shrink-0 ${logoSize}`}
                  isEditable={isEditable}
                />

                <div className="flex-1 flex flex-col items-center text-center">
                  <div className="font-black text-xl tracking-widest mb-1 uppercase flex items-center justify-center gap-1">
                    {invoice.invoice_type === "TAX" ? "TAX INVOICE" : "INVOICE"}
                    {isEditable && saving && (
                      <span className="text-[9px] text-gray-500 font-normal lowercase">
                        (saving...)
                      </span>
                    )}
                    {isEditable && saveSuccess && (
                      <span className="text-[9px] text-green-600 font-normal lowercase">
                        (saved)
                      </span>
                    )}
                  </div>

                  <EditableField
                    value={profile.name}
                    onCommit={(val) => handleInlineSave("name", val)}
                    placeholder="Business Name"
                    align="center"
                    displayClassName="font-black text-base tracking-widest uppercase w-full"
                    inputClassName="text-base font-black uppercase text-center"
                    isEditable={isEditable}
                  />

                  <div className="flex flex-col items-center gap-0.5 mt-1 text-[11px] w-full">
                    <EditableField
                      value={profile.address}
                      onCommit={(val) => handleInlineSave("address", val)}
                      placeholder="Company Address"
                      multiline
                      rows={2}
                      align="center"
                      displayClassName="text-[11px] w-full"
                      inputClassName="text-[11px]"
                      isEditable={isEditable}
                    />
                    <EditableField
                      value={profile.phone}
                      onCommit={(val) => handleInlineSave("phone", val)}
                      placeholder="Phone Number"
                      align="center"
                      displayClassName="text-[11px] w-full"
                      inputClassName="text-[11px]"
                      isEditable={isEditable}
                    />
                    <EditableField
                      value={profile.email}
                      onCommit={(val) => handleInlineSave("email", val)}
                      placeholder="Email Address"
                      align="center"
                      displayClassName="text-[11px] w-full"
                      inputClassName="text-[11px]"
                      isEditable={isEditable}
                    />
                    <EditableField
                      value={profile.gstin}
                      onCommit={(val) => handleInlineSave("gstin", val)}
                      placeholder="GSTIN"
                      align="center"
                      displayClassName="font-bold mt-0.5 text-[11px] w-full"
                      inputClassName="text-[11px] font-bold"
                      isEditable={isEditable}
                    />
                  </div>
                </div>

                <div className={`shrink-0 ${logoSize}`} />
              </div>

              <div className="whitespace-pre overflow-hidden">{line("=")}</div>

              {/* ─── META & BILL TO ─── */}
              <div className="flex flex-col text-[11px] gap-0.5 my-1">
                <div className="flex justify-between">
                  <span className="font-bold">Invoice No:</span>
                  <span>{invoice.invoice_no}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-bold">Order No:</span>
                  <span>{orderNo}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-bold">
                    {invoice.header_date_label}:
                  </span>
                  <span>
                    {fmtDate(invoice.createdAt)} {fmtTime(invoice.createdAt)}
                  </span>
                </div>
                <div className="mt-2">
                  <div className="font-bold">Bill To:</div>
                  <div>{customerName || "—"}</div>
                  {invoice.vendor_client.phone && (
                    <div>Ph: {invoice.vendor_client.phone}</div>
                  )}
                </div>
              </div>

              <div className="whitespace-pre overflow-hidden">{line("=")}</div>

              {/* ─── TABLE HEADER ─── */}
              <div className="flex font-bold text-[10px] leading-tight mb-1">
                <div className={`${colItem} text-left`}>
                  <div className="truncate">Item Name</div>
                  <div className="font-normal truncate">HSN / SAC</div>
                </div>
                {showExpiry && (
                  <div className={`${colExpiry} text-left pl-1`}>
                    <div className="truncate">Expiry</div>
                    <div className="font-normal truncate">Batch No</div>
                  </div>
                )}
                <div className={`${colQty} text-right truncate`}>Qty</div>
                <div className={`${colMrp} text-right truncate`}>MRP</div>
                <div className={`${colDisc} text-right`}>
                  <div className="truncate">Disc Amt</div>
                  <div className="font-normal truncate">Disc %</div>
                </div>
                <div className={`${colTax} text-right`}>
                  <div className="truncate">Tax Amt</div>
                  <div className="font-normal truncate">Tax %</div>
                </div>
                <div className={`${colTotal} text-right truncate`}>Total</div>
              </div>

              <div className="whitespace-pre overflow-hidden">{line("-")}</div>

              {/* ─── TABLE ROWS ─── */}
              {invoice.items.map((item, i) => {
                const gross = item.unit_price * item.quantity;
                const discPct = gross > 0 ? (item.discount / gross) * 100 : 0;

                return (
                  <React.Fragment key={item._id || i}>
                    <div className="flex text-[11px] py-1 border-b border-dashed border-gray-400 items-start">
                      <div className={`${colItem} pr-1 min-w-0`}>
                        <div
                          className="font-bold truncate"
                          title={item.service_name}
                        >
                          {item.service_name}
                        </div>
                        <div
                          className="text-[10px] text-gray-700 truncate"
                          title={item.hsn_code}
                        >
                          {item.hsn_code}
                        </div>
                      </div>
                      {showExpiry && (
                        <div className={`${colExpiry} pl-1 min-w-0`}>
                          <div
                            className="font-bold text-[10px] truncate"
                            title={item.expiry}
                          >
                            {item.expiry || "-"}
                          </div>
                          <div
                            className="text-[10px] text-gray-700 truncate"
                            title={item.batch_no}
                          >
                            {item.batch_no || "-"}
                          </div>
                        </div>
                      )}
                      <div className={`${colQty} text-right font-bold`}>
                        {item.quantity}
                      </div>
                      <div className={`${colMrp} text-right font-bold`}>
                        {fmtMoney(item.unit_price)}
                      </div>
                      <div className={`${colDisc} text-right font-bold`}>
                        <div>
                          {item.discount > 0
                            ? `${fmtMoney(item.discount)}`
                            : "-"}
                        </div>
                        <div className="text-[10px] text-gray-700">
                          {discPct > 0 ? `${Math.round(discPct)}%` : "-"}
                        </div>
                      </div>
                      <div className={`${colTax} text-right font-bold`}>
                        <div>{fmtMoney(item.gst)}</div>
                        <div className="text-[10px] text-gray-700">
                          {Math.round(item.gst_percent)}%
                        </div>
                      </div>
                      <div className={`${colTotal} text-right font-bold`}>
                        {fmtMoney(item.total_price)}
                      </div>
                    </div>

                    {item.products?.map((p, pi) => {
                      const qty = p.quantity || 1;
                      const pGross = p.unit_price * qty;
                      const pDiscPct =
                        pGross > 0 ? (p.discount / pGross) * 100 : 0;
                      return (
                        <div
                          key={p._id || pi}
                          className="flex text-[10px] py-0.5 items-start text-gray-700"
                        >
                          <div className={`${colItem} pr-1 min-w-0`}>
                            <div
                              className="truncate font-medium"
                              title={p.product_name}
                            >
                              └ {p.product_name}
                            </div>
                            <div
                              className="text-[9px] text-gray-500 truncate"
                              title={p.hsn_code}
                            >
                              {p.hsn_code}
                            </div>
                          </div>
                          {showExpiry && (
                            <div className={`${colExpiry} pl-1 min-w-0`}>
                              <div
                                className="font-bold truncate"
                                title={p.expiry}
                              >
                                {p.expiry || "-"}
                              </div>
                              <div
                                className="text-[9px] text-gray-500 truncate"
                                title={p.batch_no}
                              >
                                {p.batch_no || "-"}
                              </div>
                            </div>
                          )}
                          <div className={`${colQty} text-right font-bold`}>
                            {qty}
                          </div>
                          <div className={`${colMrp} text-right font-bold`}>
                            {fmtMoney(p.unit_price)}
                          </div>
                          <div className={`${colDisc} text-right font-bold`}>
                            <div>
                              {p.discount > 0
                                ? `-${fmtMoney(p.discount)}`
                                : "-"}
                            </div>
                            <div className="text-[9px]">
                              {pDiscPct > 0 ? `${Math.round(pDiscPct)}%` : "-"}
                            </div>
                          </div>
                          <div className={`${colTax} text-right font-bold`}>
                            <div>{fmtMoney(p.gst)}</div>
                            <div className="text-[9px]">
                              {Math.round(p.gst_percent)}%
                            </div>
                          </div>
                          <div className={`${colTotal} text-right font-bold`}>
                            {fmtMoney(p.total_price)}
                          </div>
                        </div>
                      );
                    })}

                    {item.addons?.map((a, ai) => {
                      const qty = a.quantity || 1;
                      const aGross = a.unit_price * qty;
                      const aDiscPct =
                        aGross > 0 ? (a.discount / aGross) * 100 : 0;
                      return (
                        <div
                          key={a._id || ai}
                          className="flex text-[10px] py-0.5 items-start text-gray-700"
                        >
                          <div className={`${colItem} pr-1 min-w-0`}>
                            <div
                              className="truncate font-medium"
                              title={a.name}
                            >
                              └ {a.name}
                            </div>
                            <div
                              className="text-[9px] text-gray-500 truncate"
                              title={a.hsn_code}
                            >
                              {a.hsn_code}
                            </div>
                          </div>
                          {showExpiry && (
                            <div className={`${colExpiry} pl-1 min-w-0`}>
                              <div
                                className="font-bold truncate"
                                title={a.expiry}
                              >
                                {a.expiry || "-"}
                              </div>
                              <div
                                className="text-[9px] text-gray-500 truncate"
                                title={a.batch_no}
                              >
                                {a.batch_no || "-"}
                              </div>
                            </div>
                          )}
                          <div className={`${colQty} text-right font-bold`}>
                            {qty}
                          </div>
                          <div className={`${colMrp} text-right font-bold`}>
                            {fmtMoney(a.unit_price)}
                          </div>
                          <div className={`${colDisc} text-right font-bold`}>
                            <div>
                              {a.discount > 0
                                ? `-${fmtMoney(a.discount)}`
                                : "-"}
                            </div>
                            <div className="text-[9px]">
                              {aDiscPct > 0 ? `${Math.round(aDiscPct)}%` : "-"}
                            </div>
                          </div>
                          <div className={`${colTax} text-right font-bold`}>
                            <div>{fmtMoney(a.gst)}</div>
                            <div className="text-[9px]">
                              {Math.round(a.gst_percent)}%
                            </div>
                          </div>
                          <div className={`${colTotal} text-right font-bold`}>
                            {fmtMoney(a.total_price)}
                          </div>
                        </div>
                      );
                    })}

                    {item.otherCharges?.map((oc, oi) => (
                      <div
                        key={oc._id || oi}
                        className="flex text-[10px] py-0.5 items-start text-gray-700"
                      >
                        <div
                          className={`${colItem} pr-1 min-w-0 truncate font-medium`}
                          title={oc.description}
                        >
                          └ {oc.description}
                        </div>
                        {showExpiry && (
                          <div className={`${colExpiry} pl-1 text-gray-500`}>
                            -
                          </div>
                        )}
                        <div className={`${colQty} text-right font-bold`}>
                          1
                        </div>
                        <div className={`${colMrp} text-right font-bold`}>
                          {fmtMoney(oc.amount)}
                        </div>
                        <div className={`${colDisc} text-right font-bold`}>
                          -
                        </div>
                        <div className={`${colTax} text-right font-bold`}>
                          -
                        </div>
                        <div className={`${colTotal} text-right font-bold`}>
                          {fmtMoney(oc.total_price)}
                        </div>
                      </div>
                    ))}
                  </React.Fragment>
                );
              })}

              <div className="whitespace-pre overflow-hidden">{line("=")}</div>

              {/* ─── TOTALS ─── */}
              <div className="flex justify-between items-start mt-1">
                <div className="w-[40%] pt-1">
                  <div className="font-bold text-[11px]">
                    Total Qty:{" "}
                    <span className="text-[13px]">{invoice.total_qty}</span>
                  </div>
                </div>
                <div className="w-[60%] flex flex-col items-end">
                  <div className="w-[260px] text-[11px]">
                    {/* 🟢 ONLY SHOW SUB TOTAL & DISCOUNT IF THERE IS A DISCOUNT */}
                    {invoice.total_discount > 0 && (
                      <>
                        <div className="flex justify-between text-gray-800">
                          <span>Sub Total</span>
                          <span>{fmtMoney(invoice.sub_total)}</span>
                        </div>

                        <div className="flex justify-between text-gray-800">
                          <span>Item Discount</span>
                          <span>- {fmtMoney(invoice.total_discount)}</span>
                        </div>
                      </>
                    )}

                    <div className="flex justify-between font-bold">
                      <span>Net Amount</span>
                      <span>{fmtMoney(invoice.net_amount)}</span>
                    </div>
                    <div className="flex justify-between font-bold">
                      <span>Tax</span>
                      <span>{fmtMoney(invoice.total_gst)}</span>
                    </div>
                    {invoice.round_off !== 0 && (
                      <div className="flex justify-between">
                        <span>Round Off</span>
                        <span>
                          {invoice.round_off > 0 ? "+" : "-"}
                          {fmtMoney(Math.abs(invoice.round_off))}
                        </span>
                      </div>
                    )}
                    {invoice.adjustment_amount > 0 && (
                      <div className="flex justify-between">
                        <span>Adjustment</span>
                        <span>-{fmtMoney(invoice.adjustment_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-[12px] border-t border-black mt-1 pt-1">
                      <span>Order Amount</span>
                      <span>
                        ₹{Math.round(Number(invoice.grand_total))}
                      </span>{" "}
                    </div>
                    <div className="flex justify-between mt-1">
                      <span>Payment Status</span>
                      <span className="uppercase font-bold">
                        {invoice.payment_status === "paid"
                          ? "PAID"
                          : invoice.payment_status === "partial"
                            ? "PARTIAL"
                            : "UNPAID"}
                      </span>
                    </div>
                    {invoice.balance_amount !== 0 && (
                      <div className="flex justify-between font-bold text-[12px] text-red-600">
                        <span>Balance Due</span>
                        <span>{fmtMoney(invoice.balance_amount)}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ─── PAYMENTS ─── */}
              {invoice.payments.filter(
                (p) => p.mode && p.mode.toLowerCase() !== "unpaid",
              ).length > 0 && (
                <div className="mt-2 text-[11px] flex flex-col gap-0.5 border-t border-dashed border-gray-400 pt-2">
                  <div className="font-bold">Payments:</div>
                  {invoice.payments.map((p, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="capitalize">
                        {p.mode}
                        {p.date ? ` · ${fmtDate(p.date)}` : ""}
                        {p.ref ? ` · ${p.ref}` : ""}
                      </span>
                      <span className="font-bold">{fmtMoney(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* ─── WORDS & PAYMENT MODE ─── */}
              <div className="mt-2 text-[11px] flex flex-col gap-1 border-t border-dashed border-gray-400 pt-2">
                <div>
                  <span className="font-bold">Total Amount in words: </span>
                  <span>{invoice.amount_in_words}</span>
                </div>
                <div>
                  <span className="font-bold">Payment Mode: </span>
                  <span className="uppercase">{invoice.payment_mode}</span>
                </div>
              </div>

              <div className="whitespace-pre overflow-hidden">{line("=")}</div>

              {/* ─── SIGNATURE (Editable) ─── */}
              <div className="flex justify-between text-[11px] mt-1">
                <div className="w-1/2 relative group">
                  {profile.showSignature !== false ? (
                    <div className="relative w-48">
                      {/* X Button to Remove */}
                      {isEditable && (
                        <button
                          onClick={() =>
                            handleInlineSave("showSignature", false)
                          }
                          className="no-print absolute -top-4 -right-2 text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-0.5 shadow-sm border border-gray-300"
                          title="Remove Signature"
                        >
                          <svg
                            className="w-3 h-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            ></path>
                          </svg>
                        </button>
                      )}

                      <div className="h-10" />
                      <div className="border-t border-gray-600 w-full pt-1">
                        Authorised Signatory
                      </div>
                    </div>
                  ) : (
                    /* Button to bring it back */
                    isEditable && (
                      <button
                        onClick={() => handleInlineSave("showSignature", true)}
                        className="no-print text-[10px] text-blue-500 hover:underline bg-gray-100 p-1 rounded border border-gray-300"
                      >
                        + Add Signature
                      </button>
                    )
                  )}
                </div>
                <div className="text-right">
                  <div className="mt-1 text-gray-500">
                    Date and Time {fmtDate(new Date())} {fmtTime(new Date())}
                  </div>
                </div>
              </div>

              <div className="whitespace-pre overflow-hidden">{line("=")}</div>
            </div>

            {printer.showTractor && <Tractor />}
          </div>
        </div>
      </div>
    </div>
  );
}
